import { api } from "@breakout/backend/convex/_generated/api"
import { convexQuery } from "@convex-dev/react-query"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { zodSearchValidator } from "@tanstack/router-zod-adapter"
import { motion } from "motion/react"
import { useCallback } from "react"
import { z } from "zod"
import { type SortDirection, type SortField, WeekView } from "../components/home/WeekView"
import { WeekViewSkeleton } from "../components/home/WeekViewSkeleton"
import { PageContainer } from "../components/layout/PageContainer"
import { XLogo } from "../components/ui/XLogo"

const homeSearchSchema = z.object({
	sort: z.enum(["mindshare", "liquidity"]).optional().catch(undefined),
	dir: z.enum(["asc", "desc"]).optional().catch(undefined),
})

export const Route = createFileRoute("/")({
	component: HomePage,
	validateSearch: zodSearchValidator(homeSearchSchema),
})

function HomePage() {
	const { sort, dir } = Route.useSearch()
	const navigate = useNavigate()

	// Single query returns all data needed for the home page
	const { data, isLoading } = useQuery(convexQuery(api.public.home.getWeekViewData, {}))

	const handleSortChange = useCallback(
		(field: SortField, direction: SortDirection) => {
			// Don't add params for default (liquidity desc)
			if (field === "liquidity" && direction === "desc") {
				void navigate({ to: "/", search: {} })
			} else {
				void navigate({ to: "/", search: { sort: field, dir: direction } })
			}
		},
		[navigate],
	)

	return (
		<div className="bg-bg-primary text-text-primary">
			{/* Mobile CT Timeline link — shown in page content on mobile, header handles desktop */}
			<div className="flex justify-center py-5 sm:hidden">
				<Link
					className="inline-flex items-center gap-2 rounded-full bg-bg-secondary px-5 py-2.5 font-medium text-[15px] text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary active:scale-[0.97]"
					to="/timeline"
				>
					<XLogo aria-hidden="true" className="size-4" />
					<span>CT Bangers</span>
				</Link>
			</div>

			<PageContainer className="pt-0 pb-8 sm:pt-8">
				{isLoading ? (
					<WeekViewSkeleton />
				) : data ? (
					<motion.div
						animate={{ opacity: 1, y: 0 }}
						initial={{ opacity: 0, y: 8 }}
						transition={{ duration: 0.3, ease: "easeOut" }}
					>
						<WeekView
							creators={data.creators}
							ffgMarket={data.ffgMarket}
							onSortChange={handleSortChange}
							sortDirection={dir}
							sortField={sort}
							week={data.week}
						/>
					</motion.div>
				) : (
					<div className="rounded-2xl border border-border border-dashed bg-bg-card py-16 text-center shadow-sm">
						<p className="font-medium text-text-muted">No active week</p>
						<p className="mt-2 text-sm text-text-muted">
							Check back later for the next round of prediction markets.
						</p>
					</div>
				)}
			</PageContainer>
		</div>
	)
}
